import isEmpty from 'lodash/isEmpty';
import { getRequestDataAsUrlParams, suppressedStatuses } from './request';

// access to webplugin native interface and Pollfish API
Pollfish.mobile.interface.webplugin = (function webplugin() {
  const ravenBreadcrumb = (category) => {
    Raven.captureBreadcrumb({
      message: 'interface.webplugin',
      category,
    });
  };

  return {
    // mark a user as not eligible for the survey
    userNotEligible() {
      ravenBreadcrumb('userNotEligible');
      window.parent.postMessage('userNotEligible', '*');
    },

    // send the device data after user consent
    sentDataOfUserConsentToServer(url) {
      ravenBreadcrumb('sentDataOfUserConsentToServer', { url });
      // do nothing
    },

    // close panel and show indicator
    close() {
      ravenBreadcrumb('close');
      window.parent.postMessage('close', '*');
    },

    // close panel and don't show indicator
    closeAndNoShow() {
      ravenBreadcrumb('closeAndNoShow');
      window.parent.postMessage('closeAndNoShow', '*');
    },

    // inform SDK that the survey is finished
    setSurveyCompleted(payload) {
      ravenBreadcrumb('setSurveyCompleted', { payload });
      // NOTE: maintaining support for older integrations that expected no data
      if (isEmpty(payload)) {
        window.parent.postMessage('setSurveyCompleted', '*');
        return;
      }
      const eventData = {
        type: 'setSurveyCompleted',
        ...payload,
      };
      window.parent.postMessage(JSON.stringify(eventData), '*');
    },

    // present simple pop-up native message
    showToastMsg(msg) {
      ravenBreadcrumb('showToastMsg', { msg });
      // do nothing
    },

    // open website in native browser out of app
    openWebsite(url) {
      ravenBreadcrumb('openWebsite', { url });
      window.open(url);
    },

    // SDK Webview only. Not currently used
    hideMediationViews() {
      ravenBreadcrumb('hideMediationViews');
      // do nothing
    },

    userRejectedSurvey() {
      ravenBreadcrumb('userRejectedSurvey');
      window.parent.postMessage('userRejectedSurvey', '*');
    },

    noSurveyFound() {
      ravenBreadcrumb('noSurveyFound');
      window.parent.postMessage('noSurveyFound', '*');
    },

    // notify SDK when everything is loaded
    webViewLoaded() {
      ravenBreadcrumb('webViewLoaded');

      if (Pollfish.helper.json.containsSurvey) {
        // populate surveyInfo data and then call webViewLoaded so that data are available too
        const isPlayful = Pollfish.helper.json.theme.style !== 0;
        const eventData = {
          type: 'surveyInfo',
          s_id: Pollfish.helper.json.s_id,
          revenue: Pollfish.helper.json.publisherRevenue,
          playful: isPlayful,
          survey_loi: Pollfish.helper.json.survey_loi,
          survey_ir: Pollfish.helper.json.survey_ir,
          survey_class: Pollfish.helper.json.survey_class,
          remaining_completes: Pollfish.helper.json.remaining_completes,
        };
        if (Pollfish.helper.json.reward_name) {
          eventData.reward_name = Pollfish.helper.json.reward_name;
        }
        if (Pollfish.helper.json.reward_value) {
          eventData.reward_value = Pollfish.helper.json.reward_value;
        }
        window.parent.postMessage(JSON.stringify(eventData), '*');
      }
      window.parent.postMessage('webViewLoaded', '*');
    },

    // get data from server in json format
    getFromServer(responseCallback) {
      ravenBreadcrumb('getFromServer');
      responseCallback(JSON.stringify(Pollfish.helper.html.json));
    },

    // clear the queue cache
    clearCacheQueue() {
      ravenBreadcrumb('clearCacheQueue');
      // do nothing
    },

    // send information to server
    sendToServer(url, data, saveAtQueue) {
      ravenBreadcrumb('sendToServer', { url, data, saveAtQueue });
      // create json from data string for easier manipulation

      // fixes [PUB-546]
      const baseURL = Pollfish.helper.getBaseRequestURL();

      const json = JSON.parse(data);
      const queryParams = getRequestDataAsUrlParams(json);
      const requestURL = baseURL + url;
      // send ajax request to server
      $.ajax({
        // url to send POST request
        url: requestURL,

        // type of AJAX request is POST
        type: 'POST',
        tryCount: 0,
        retryLimit: 3,
        // data to be send
        data: queryParams,

        error: function onError(jqXHR, textStatus, errorThrown) {
          const statusCode = jqXHR.status;
          const { responseText } = jqXHR;
          if (statusCode === 504 || textStatus === 'timeout') {
            this.tryCount += 1;
            if (this.tryCount <= this.retryLimit) {
              $.ajax(this);
              return;
            }
          }
          // statusCode zeroes are suppressed because they give no proper info and
          // continue to fill up our sentry account
          if (statusCode === 0 || statusCode === '0') {
            Pollfish.analytics.sendEvent('error', 'statusCodeZero', requestURL);
            return;
          }

          if (suppressedStatuses.indexOf(statusCode) > -1) {
            Pollfish.analytics.sendEvent('http-error', statusCode, requestURL);
            return;
          }

          Pollfish.analytics.sendEvent('error', 'sendToServer', requestURL);
          Raven.captureException(`[HTTP-ERROR]:${statusCode}`, {
            fingerprint: [statusCode],
            tags: {
              requestURL,
            },
            extra: {
              textStatus,
              statusCode,
              responseText,
              url: requestURL,
              errorThrown,
              data: queryParams,
              interface: 'sendToServer.webplugin',
            },
          });
        },
      });
    },

    // get device information to handle js and css compatibility
    getDeviceInfo(responseCallback) {
      ravenBreadcrumb('getDeviceInfo');
      // return data provided by server
      responseCallback(JSON.stringify(Pollfish.helper.html.deviceInfo));
    },

    // notify SDK that the focus is no the input
    textFieldFocus() {
      ravenBreadcrumb('textFieldFocus');
      // do nothing
    },

    // notify SDK that the focus is lost from input
    textFieldUnFocus() {
      ravenBreadcrumb('textFieldUnFocus');
      // do nothing
    },

    // notify SDK that the video has ended
    notifyVideoEnd() {
      ravenBreadcrumb('notifyVideoEnd');
      // do nothing
    },

    // interface for calls from SDK start

    // ios only focus on input of email for winner
    focusExplicitly(browserHeight) {
      ravenBreadcrumb('focusExplicitly', { browserHeight });
      // do nothing
    },

    // remove focus from input of email for winner
    loseFocus(emailInputFocus) {
      ravenBreadcrumb('loseFocus', { emailInputFocus });
      // do nothing
    },

    panelClosed() {
      ravenBreadcrumb('panelClosed');
      // do nothing
    },

    panelOpened() {
      ravenBreadcrumb('panelOpened');
      const body = document.querySelector('body');
      const isUnlockScreen = body.dataset.page === 'unlock-screen';
      if (
        Pollfish.helper.isOfferwall() &&
        Pollfish.helper.getOfferwallSurveysEnabled() &&
        Pollfish.offerwallPage &&
        !Pollfish.survey &&
        !isUnlockScreen
      ) {
        Pollfish.offerwallPage.getSurveys();
      }
    },

    playVideo() {
      ravenBreadcrumb('playVideo');
      // do nothing
    },

    pauseVideo() {
      ravenBreadcrumb('pauseVideo');
      // do nothing
    },

    webViewFocus(mode) {
      ravenBreadcrumb('webViewFocus', { mode });
      // do nothing
    },

    // ios only function to exit full screen
    exitFullScreen(mode) {
      ravenBreadcrumb('exitFullScreen', { mode });
      // do nothing
    },

    // interface for calls from SDK end
  };
})();

export default Pollfish.mobile.interface.webplugin;
